/** @jsxRuntime classic */
/** @jsx jsx */
import { jsx } from '@emotion/core';
import React, { useRef, useState } from 'react';
import { Button, Modal, Tabs, Typography } from 'antd';
import type { ModalProps } from 'antd/lib/modal';
import { useIntl } from 'react-intl';
import { getEvaluationCurrentInstantMessageEmailPreview } from '~src/services/api/offers';
import {
  getGlobalEvaluationInstantMessageEmailPreview,
  getGlobalEvaluationInstantMessageEmailPreviewVendor,
} from '~src/services/api/movements';
import { PreviewTab } from '~components/popups-modals/MovementsModals/Tabs/PreviewTab.modal';
import { RecipientsTab } from './Tabs/RecipientsTab';
import { MessageDraftTab } from './Tabs/MessageDraftTab';
import { HistoryTab } from './Tabs/HistoryTab';
import { ThemedStyleFn, useStyles } from '~src/hooks/useStyles';

type OffersMessagesTabKey = 'recipients' | 'draft' | 'preview' | 'history';

export interface OffersMessagesModalProps extends ModalProps {
  projectId: number;
  tenderId: number;
  contract?: any;
  readOnly?: boolean;
  readOnlyData?: any;
  hidePreview?: boolean;
  shareId?: string;
  shareKey?: string;
}

export const OffersMessagesModal: React.FC<OffersMessagesModalProps> = ({
  visible,
  onCancel,
  projectId,
  tenderId,
  contract,
  readOnly,
  readOnlyData,
  hidePreview,
  shareId,
  shareKey,
  ...modalProps
}) => {
  const intl = useIntl();
  const styles = useStyles(styleFn);
  const messageDraftTabRef = useRef<any>(null);
  const [resetNonce, setResetNonce] = useState(0);

  const contractId = contract?.id as number;

  const [activeTab, setActiveTab] = useState<OffersMessagesTabKey>('draft');
  const [isDirty, setIsDirty] = useState(false);
  const [canSend, setCanSend] = useState(false);
  const [isSending, setIsSending] = useState(false);

  // Handle modal close with unsaved changes warning
  const handleRequestClose = () => {
    if (readOnly) {
      onCancel?.({} as any);
      return;
    }

    if (!isDirty) {
      onCancel?.({} as any);
      return;
    }

    Modal.confirm({
      title: intl.formatMessage({ id: 'unsaved-changes' }),
      content: intl.formatMessage({ id: 'unsaved-changes-message' }),
      okText: intl.formatMessage({ id: 'discard' }),
      cancelText: intl.formatMessage({ id: 'stay' }),
      okType: 'danger',
      onOk: () => onCancel?.({} as any),
    });
  };

  // Handle tab switching with unsaved changes warning
  const handleTabChange = (newTab: string) => {
    if (readOnly) {
      setActiveTab(newTab as OffersMessagesTabKey);
      return;
    }

    if (!isDirty) {
      setActiveTab(newTab as OffersMessagesTabKey);
      return;
    }

    // If leaving the draft tab with unsaved changes, warn
    if (activeTab === 'draft' && newTab !== 'draft') {
      Modal.confirm({
        title: intl.formatMessage({ id: 'unsaved-changes' }),
        content: intl.formatMessage({
          id: 'unsaved-changes-tab-switch-message',
        }),
        okText: intl.formatMessage({ id: 'discard' }),
        cancelText: intl.formatMessage({ id: 'stay' }),
        okType: 'danger',
        onOk: () => {
          setResetNonce((prev) => prev + 1);
          setActiveTab(newTab as OffersMessagesTabKey);
        },
      });
    } else {
      setActiveTab(newTab as OffersMessagesTabKey);
    }
  };

  // Handle send button click
  const handleSend = () => {
    messageDraftTabRef.current?.sendMessage();
  };

  // Handle successful send - close modal
  const handleSendSuccess = () => {
    onCancel?.({} as any);
  };

  // Preview configuration
  const previewId = readOnly
    ? readOnlyData?.message_id || readOnlyData?.id
    : contractId;

  const previewFn = readOnly
    ? ({
        project_id,
        tender_id,
        lang,
      }: {
        project_id: number;
        tender_id: number;
        lang: string;
      }) => {
        const instantMessageId = (readOnlyData?.message_id ||
          readOnlyData?.id) as number;

        if (shareId && shareKey) {
          return getGlobalEvaluationInstantMessageEmailPreviewVendor({
            shareId,
            shareKey,
            instantMessageId,
            lang,
          });
        }

        return getGlobalEvaluationInstantMessageEmailPreview({
          projectId: project_id,
          tenderId: tender_id,
          instantMessageId,
          lang,
        });
      }
    : ({
        project_id,
        tender_id,
        lang,
      }: {
        project_id: number;
        tender_id: number;
        lang: string;
      }) =>
        getEvaluationCurrentInstantMessageEmailPreview({
          projectId: project_id,
          tenderId: tender_id,
          contractId,
          lang,
        });

  return (
    <Modal
      title={
        <div css={styles.modalTitle}>
          <div css={styles.titleText}>
            {intl.formatMessage({
              id: 'offers-message',
            })}
          </div>

          <Typography.Text type="secondary" css={styles.headerHint}>
            {contract?.reference ? `#${contract.reference} - ` : ''}
            {contract?.name || intl.formatMessage({ id: 'no-reference' })}
          </Typography.Text>
        </div>
      }
      visible={visible}
      width="min(88vw, 980px)"
      bodyStyle={{ padding: '18px 22px 0 22px' }}
      centered
      destroyOnClose={true}
      maskClosable={false}
      onCancel={handleRequestClose}
      footer={
        readOnly
          ? [
              <Button key="close" onClick={handleRequestClose}>
                {intl.formatMessage({ id: 'close' })}
              </Button>,
            ]
          : [
              <Button key="close" onClick={handleRequestClose}>
                {intl.formatMessage({ id: 'close' })}
              </Button>,

              <Button
                key="send"
                type="primary"
                disabled={!canSend}
                loading={isSending}
                onClick={handleSend}>
                {isDirty
                  ? intl.formatMessage({
                      id: 'save-and-send',
                    })
                  : intl.formatMessage({
                      id: 'send',
                    })}
              </Button>,
            ]
      }
      {...modalProps}>
      <Tabs activeKey={activeTab} onChange={handleTabChange} css={styles.tabs}>
        {/* ── RECIPIENTS TAB (hidden in read-only mode) ── */}
        {!readOnly && (
          <Tabs.TabPane
            key="recipients"
            tab={intl.formatMessage({
              id: 'recipients',
            })}
            forceRender={false}>
            <RecipientsTab
              projectId={projectId}
              tenderId={tenderId}
              contractId={contractId}
              visible={!!visible}
            />
          </Tabs.TabPane>
        )}

        {/* ── DRAFT TAB ── */}
        <Tabs.TabPane
          key="draft"
          tab={intl.formatMessage({
            id: 'offers-message-draft',
          })}>
          <MessageDraftTab
            ref={messageDraftTabRef}
            projectId={projectId}
            tenderId={tenderId}
            contractId={contractId}
            contract={contract}
            visible={!!visible}
            readOnly={!!readOnly}
            shareId={shareId}
            shareKey={shareKey}
            readOnlyData={readOnlyData}
            onDirtyChange={setIsDirty}
            onCanSendChange={setCanSend}
            onSendSuccess={handleSendSuccess}
            onSendingChange={setIsSending}
            resetNonce={resetNonce}
          />
        </Tabs.TabPane>

        {/* ── PREVIEW TAB (hidden when hidePreview=true, e.g. vendor side with no preview API) ── */}
        {!hidePreview && (
          <Tabs.TabPane
            key="preview"
            tab={intl.formatMessage({
              id: 'correction-letter-tab-preview',
            })}>
            <PreviewTab
              projectId={projectId}
              tenderId={tenderId}
              previewId={previewId}
              hideModeSelector={true}
              getPreview={previewFn}
              getModificationReportPreview={previewFn}
            />
          </Tabs.TabPane>
        )}

        {/* ── HISTORY TAB (only visible when opened from Offers tab, not in read-only mode) ── */}
        {!readOnly && (
          <Tabs.TabPane
            key="history"
            tab={intl.formatMessage({
              id: 'history',
            })}>
            <HistoryTab
              projectId={projectId}
              tenderId={tenderId}
              visible={!!visible}
              readOnly={!!readOnly}
            />
          </Tabs.TabPane>
        )}
      </Tabs>
    </Modal>
  );
};

const styleFn: ThemedStyleFn = () => ({
  modalTitle: {
    display: 'flex',
    flexDirection: 'column',
    gap: 2,
  },

  titleText: {
    fontWeight: 700,
    fontSize: 16,
  },

  headerHint: {
    fontSize: 12,
  },

  tabs: {
    '.ant-tabs-nav': {
      marginBottom: 16,
    },
  },
});
